import React from 'react'

import Guid from '../../../utils/guid.js'

import {Button, IconButton, Modal, ModalHeader, ModalActions, Popup, Colors, P} from '../UI/index.js'
import {formSpacing, FormRow, FormStack, Input, CurrencyInput, Select} from '../UI/forms/index.js'
import {Reseller} from '../../../types/types.js'
import {Activity, ShiftPriceTable} from 'server/functions/resellers/resellers.types.js'
import DividerHeader from '@components/UI/surfaces/DividerHeader.js'
import Languages from '@utils/Languages.js'
import type {PopupRef} from '../UI/overlays/Popup.js'

interface ShiftPriceTableModalProps {
    reseller: Reseller
    onSubmit: (priceTable: ShiftPriceTable) => void
}

interface ShiftPriceTableModalState {
    modalIsOpen: boolean
    priceTable: ShiftPriceTable
    newPriceTable: boolean
    validation: {
        name?: boolean
    }
}

const newShiftPriceTable = (): ShiftPriceTable => ({
    id: Guid.generate(),
    name: '',
    priceBasedOn: 'workedTime',
    pricePerHour: '0,00',
    activities: {},
    startRate: '0,00',
    minimumRate: '0,00',
    rounding: ''
})

export default class ShiftPriceTableModal extends React.Component<ShiftPriceTableModalProps, ShiftPriceTableModalState> {
    popup: PopupRef

    constructor(props: ShiftPriceTableModalProps) {
        super(props)

        this.state = {
            modalIsOpen: false,
            priceTable: newShiftPriceTable(),
            newPriceTable: true,
            validation: {}
        }
    }

    open(priceTable?: ShiftPriceTable) {
        this.setState({
            modalIsOpen: true,
            newPriceTable: !priceTable,
            priceTable: priceTable ? {...newShiftPriceTable(), ...structuredClone(priceTable)} : newShiftPriceTable(),
            validation: {}
        })
    }

    close() {
        this.setState({modalIsOpen: false})
    }

    onChangeValue(key: keyof ShiftPriceTable, event: {target: {value: string}}) {
        const priceTable = {...this.state.priceTable}

        if (key === 'rounding') {
            priceTable.rounding = event.target.value === '' ? '' : parseFloat(event.target.value)
        } else {
            (priceTable as Record<string, unknown>)[key] = event.target.value
        }

        this.setState({priceTable})
    }

    onSubmit(event: React.FormEvent) {
        event.preventDefault()

        const {priceTable} = this.state

        if (!priceTable.name) {
            this.setState({validation: {name: true}})
            document.getElementById('shiftPriceTableName')?.focus()
            return
        }

        this.props.onSubmit(priceTable)
        this.close()
    }

    render() {
        const {modalIsOpen, priceTable, newPriceTable, validation} = this.state
        const {reseller} = this.props

        const activitiesObject: Record<string, Activity> = {}
        reseller.settings.shifts?.activities?.map((activity) => {
            activitiesObject[activity.id] = activity
        })

        const activityIds = Object.keys(priceTable.activities).filter((id) => activitiesObject[id])
        const availableActivities = reseller.settings.shifts?.activities?.filter((activity) => !priceTable.activities[activity.id]) || []

        return (
            <Modal show={modalIsOpen} onClose={this.close.bind(this)}>
                <ModalHeader title={newPriceTable ? 'Nieuwe tarieftabel' : 'Bewerk tarieftabel'} onClose={this.close.bind(this)} style={{marginBottom: 0}} />

                <br />
                <form onSubmit={this.onSubmit.bind(this)}>
                    <FormStack style={{marginBottom: formSpacing.rowGap}}>
                        <Input
                            id='shiftPriceTableName'
                            label='Naam'
                            value={priceTable.name}
                            isInvalid={validation.name}
                            onChange={this.onChangeValue.bind(this, 'name')}
                        />

                        <Select
                            label={Languages.priceBasedOn['NL']}
                            value={priceTable.priceBasedOn}
                            onChange={this.onChangeValue.bind(this, 'priceBasedOn')}
                            options={[
                                {value: 'workedTime', title: Languages.workedTime['NL']},
                                {value: 'activities', title: Languages.activities['NL']}
                            ]}
                        />

                        {priceTable.priceBasedOn === 'workedTime' &&
                            <CurrencyInput
                                label='Uurtarief'
                                value={priceTable.pricePerHour}
                                onChange={this.onChangeValue.bind(this, 'pricePerHour')}
                            />
                        }

                        {priceTable.priceBasedOn === 'activities' && activityIds.map((id) => {
                            return (
                                <FormRow key={`activity${id}`}>
                                    <div style={{flex: 1, borderRadius: 12, display: 'flex', alignItems: 'center', paddingLeft: 12, height: 42, background: activitiesObject[id].color}}>
                                        <P onDark={Colors.isDarkBackground(activitiesObject[id].color)}>{activitiesObject[id].name}</P>
                                    </div>

                                    <CurrencyInput
                                        label='Uurtarief'
                                        value={priceTable.activities[id]}
                                        onChange={(event) => {
                                            const priceTable = {...this.state.priceTable}
                                            priceTable.activities = {...priceTable.activities, [id]: event.target.value}
                                            this.setState({priceTable})
                                        }}
                                    />

                                    <IconButton
                                        style={{marginLeft: 12}}
                                        onClick={() => {
                                            const priceTable = {...this.state.priceTable}
                                            priceTable.activities = {...priceTable.activities}
                                            delete priceTable.activities[id]
                                            this.setState({priceTable})
                                        }}
                                    >
                                        <i className='mdi mdi-delete' />
                                    </IconButton>
                                </FormRow>
                            )
                        })}
                    </FormStack>

                    {priceTable.priceBasedOn === 'activities' && availableActivities.length > 0 &&
                        <Button
                            style={{marginTop: 12}}
                            variant='outline'
                            icon='mdi mdi-plus'
                            onClick={async () => {
                                const option = await this.popup.openOptions('Kies activiteit', '', availableActivities.map((activity) => ({
                                    title: activity.name,
                                    value: activity.id,
                                    wrapperStyle: {background: activity.color, borderRadius: 12, marginBottom: 6},
                                    style: {color: Colors.isDarkBackground(activity.color) ? Colors.white : Colors.textDark}
                                })))
                                if (!option) return

                                const {value: activityId} = option

                                const priceTable = {...this.state.priceTable}
                                priceTable.activities = {...priceTable.activities, [activityId]: '0,00'}
                                this.setState({priceTable})
                            }}
                        />
                    }

                    <DividerHeader
                        defaultHidden={true}
                        style={{marginBottom: 18, marginTop: 24}}
                        title='Overige'
                    >
                        <FormStack style={{marginBottom: formSpacing.rowGap}}>
                            <FormRow>
                                <CurrencyInput
                                    label='Starttarief'
                                    value={priceTable.startRate}
                                    onChange={this.onChangeValue.bind(this, 'startRate')}
                                />

                                <CurrencyInput
                                    label='Minimum prijs'
                                    value={priceTable.minimumRate}
                                    onChange={this.onChangeValue.bind(this, 'minimumRate')}
                                />
                            </FormRow>

                            <Select
                                label='Afronding totaal'
                                allowEmpty
                                value={priceTable.rounding ?? ''}
                                options={[
                                    {value: 1, title: 'Hele euro\'s'},
                                    {value: 2.5, title: 'Veelvoud van €2,50'},
                                    {value: 5, title: 'Veelvoud van €5,00'},
                                    {value: 10, title: 'Veelvoud van €10,00'},
                                    {value: 5.05, title: 'Veelvoud van €5,00 - €0,05'},
                                    {value: 10.05, title: 'Veelvoud van €10,00 - €0,05'}
                                ]}
                                onChange={this.onChangeValue.bind(this, 'rounding')}
                            />
                        </FormStack>
                    </DividerHeader>

                    <ModalActions>
                        <Button type='submit'>
                            Opslaan
                        </Button>
                    </ModalActions>
                </form>

                <Popup
                    ref={(ref) => this.popup = ref}
                />
            </Modal>
        )
    }
}
